import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexPermissionsUserMetadata } from 'features/permissions/hooks/useRolodexPermissionsUserMetadata'
import { fetchRolodexV3UserScopes } from 'features/permissions/utils/fetchRolodexV3UserScopes'
import {
  type CantileverAccessScopeEvaluation,
  evaluateUserAccessScope,
  type RolodexV3UserScope,
} from 'features/permissions/utils/policyScopeEvaluation'
import { useMemo } from 'react'
import useSWR from 'swr'

interface UseCurrentUserScopesResult {
  /** The current user's Manage Access scopes; undefined while loading. */
  scopes: readonly RolodexV3UserScope[] | undefined
  scopeEvaluation: CantileverAccessScopeEvaluation
  isLoading: boolean
  error: Error | undefined
}

export function useCurrentUserScopes(
  currentOrgId?: string
): UseCurrentUserScopesResult {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated, authorization } = useGeckoAuth()
  const { data: currentUserMetadata } = useRolodexPermissionsUserMetadata(
    authorizedAxios,
    authorization,
    ROLODEX_URL
  )

  const userId = currentUserMetadata?.id
  const shouldFetch = Boolean(isAuthenticated && ROLODEX_URL && userId)

  const { data, error, isLoading } = useSWR(
    userId && shouldFetch
      ? (['current-user-scopes', ROLODEX_URL, userId] as const)
      : null,
    ([, rolodexUrl, id]) =>
      fetchRolodexV3UserScopes(authorizedAxios, rolodexUrl, id),
    { revalidateOnFocus: false }
  )

  const scopeEvaluation = useMemo(
    () => evaluateUserAccessScope(data ?? [], currentOrgId),
    [data, currentOrgId]
  )

  return {
    scopes: data,
    scopeEvaluation,
    isLoading: isLoading && shouldFetch,
    error,
  }
}
